import { useState } from 'react';
import {
	Plus,
	Search,
	Pencil,
	Trash2,
	AlertTriangle,
	CreditCard,
	Users,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui';
import { CopyWrapper } from '@/components/ui/CopyWrapper';
import { useGetPlans } from '@/services/queries/company';
import { useRemovePlan } from '@/services/mutations/company';
import { CompanyPlanModal } from './components/CompanyPlanModal';

const DeleteModal = ({
	isOpen,
	onClose,
	onConfirm,
	title,
	description,
}: {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void;
	title: string;
	description: string;
}) => {
	if (!isOpen) return null;
	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
			/>
			<div className="animate-in zoom-in-95 relative w-full max-w-[400px] rounded-[2rem] bg-white p-8 text-center shadow-2xl duration-200">
				<div className="mb-4 flex justify-center text-rose-500">
					<div className="rounded-full bg-rose-50 p-4">
						<AlertTriangle size={32} />
					</div>
				</div>
				<h3 className="mb-2 text-xl font-black text-stone-900">
					{title}
				</h3>
				<p
					className="mb-8 text-sm font-medium leading-relaxed text-stone-500"
					dangerouslySetInnerHTML={{ __html: description }}
				/>
				<div className="flex flex-col gap-3">
					<Button
						text="Confirmar exclusão"
						className="w-full justify-center rounded-full border-none bg-rose-600 py-3 font-bold text-white shadow-md hover:bg-rose-700"
						onClick={onConfirm}
					/>
					<button
						onClick={onClose}
						className="text-sm font-bold text-stone-400 transition-colors hover:text-stone-600"
					>
						Cancelar
					</button>
				</div>
			</div>
		</div>,
		document.body,
	);
};

const PlanCard = ({
	plan,
	onEdit,
	onDelete,
}: {
	plan: Companies.Plan;
	onEdit: (p: Companies.Plan) => void;
	onDelete: (p: Companies.Plan) => void;
}) => (
	<div className="group relative flex h-fit flex-col overflow-hidden rounded-2xl border border-stone-100 bg-white shadow-sm transition-all hover:shadow-md">
		<div className="space-y-4 p-5">
			<div className="flex items-start justify-between">
				<div
					className={`flex items-center gap-1.5 rounded-md border px-2 py-1 ${
						plan.active
							? 'border-emerald-100 bg-emerald-50 text-emerald-600'
							: 'border-stone-200 bg-stone-50 text-stone-500'
					}`}
				>
					<span className="text-[10px] font-black uppercase tracking-widest">
						{plan.active ? 'Ativo' : 'Inativo'}
					</span>
				</div>
				<div className="flex gap-1">
					<button
						onClick={() => onEdit(plan)}
						className="rounded-lg p-2 text-stone-400 transition-all hover:bg-stone-50 hover:text-stone-900"
					>
						<Pencil size={16} />
					</button>
					<button
						onClick={() => onDelete(plan)}
						className="rounded-lg p-2 text-stone-400 transition-all hover:bg-rose-50 hover:text-rose-600"
					>
						<Trash2 size={16} />
					</button>
				</div>
			</div>
			<h3 className="text-xl font-bold leading-tight text-stone-900">
				{plan.name}
			</h3>

			<p className="mt-1 line-clamp-3 min-h-[3.65rem] text-xs leading-relaxed text-stone-400">
				{plan.description || 'Sem descrição'}
			</p>

			<div className="flex flex-wrap gap-4 border-t border-stone-50 pt-4">
				{(() => {
					const currency = plan.currency || 'BRL';
					const formatMoney = (value: number) =>
						new Intl.NumberFormat('pt-BR', {
							style: 'currency',
							currency,
							currencyDisplay: 'symbol',
						}).format(value);
					const base = Number(plan.base_cost || 0);
					const maintenance = 0;
					const subsidy = Number(plan.subsidy_per_employee || 0);
					const baseAndMaintenance = base + maintenance;

					return (
						<>
							<div>
								<p className="mb-1 text-[9px] font-black uppercase leading-none tracking-widest text-stone-300">
									Base + Manutenção
								</p>
								<p className="flex items-center gap-1.5 text-lg font-black text-stone-900">
									<CreditCard size={16} />
									{formatMoney(baseAndMaintenance)}
								</p>
							</div>
							<div>
								<p className="mb-1 text-[9px] font-black uppercase leading-none tracking-widest text-stone-300">
									Subsídio por funcionário
								</p>
								<p className="flex items-center gap-1.5 text-lg font-black text-stone-900">
									<CreditCard size={16} />
									{formatMoney(subsidy)}
								</p>
							</div>
							<div>
								<p className="mb-1 text-[9px] font-black uppercase leading-none tracking-widest text-stone-300">
									Máx. funcionários
								</p>
								<p className="flex items-center gap-1.5 text-lg font-black text-stone-900">
									<Users size={16} />
									{plan.max_employees}
								</p>
							</div>
						</>
					);
				})()}
			</div>
		</div>
		<div className="mt-auto flex flex-col gap-2 border-t border-stone-100 bg-stone-50/50 px-5 py-4">
			<CopyWrapper
				valueToCopy={plan.id}
				wrapperClassName="flex w-full items-center pr-8"
				buttonClassName="!right-0"
			>
				<span className="block truncate text-[10px] font-black uppercase tracking-widest text-stone-900">
					ID: {plan.id}
				</span>
			</CopyWrapper>
		</div>
	</div>
);

export const CompanyPlansPage = () => {
	const { data: plans, isLoading, isError } = useGetPlans();
	const { mutateAsync: removePlan } = useRemovePlan(() => {
		setIsDeleteModalOpen(false);
	});
	const [searchTerm, setSearchTerm] = useState('');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [selectedPlan, setSelectedPlan] = useState<Companies.Plan | null>(
		null,
	);

	const list = Array.isArray(plans) ? plans : [];
	const filteredPlans = list.filter((p) =>
		p.name.toLowerCase().includes(searchTerm.toLowerCase()),
	);

	const handleDeleteConfirm = async () => {
		if (selectedPlan) {
			await removePlan(selectedPlan.id);
			setIsDeleteModalOpen(false);
			setSelectedPlan(null);
		}
	};

	if (isLoading) {
		return (
			<div className="flex h-[400px] items-center justify-center">
				<p className="animate-pulse text-sm font-black uppercase tracking-widest text-stone-400">
					Carregando planos...
				</p>
			</div>
		);
	}

	if (isError) {
		return (
			<div className="flex h-[400px] flex-col items-center justify-center gap-4 text-center">
				<AlertTriangle size={48} className="text-rose-500" />
				<p className="text-sm font-bold text-stone-600">
					Erro ao carregar os planos.
				</p>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div className="relative w-full sm:max-w-md">
					<Search
						className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
						size={18}
					/>
					<input
						type="text"
						placeholder="Buscar plano..."
						className="w-full rounded-full border-2 border-zinc-100 bg-white py-2.5 pl-10 pr-4 text-sm font-medium text-stone-900 shadow-sm outline-none transition-all placeholder:text-zinc-400 focus:border-stone-900 focus:ring-2 focus:ring-stone-900/10"
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
					/>
				</div>
				<Button
					text="Novo plano"
					className="w-full rounded-full bg-black px-6 py-2 font-bold shadow-md hover:bg-neutral-800 sm:w-auto"
					startIcon={
						<Plus className="size-5 stroke-[3] text-white" />
					}
					onClick={() => {
						setSelectedPlan(null);
						setIsModalOpen(true);
					}}
				/>
			</div>

			<div className="space-y-1">
				<h1 className="text-2xl font-black tracking-tight text-stone-900">
					Planos de empresas
				</h1>
				<p className="text-sm text-stone-400">
					Gerencie os planos empresariais disponíveis para
					contratação.
				</p>
			</div>

			{filteredPlans.length > 0 ? (
				<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
					{filteredPlans.map((plan) => (
						<PlanCard
							key={plan.id}
							plan={plan}
							onEdit={(p) => {
								setSelectedPlan(p);
								setIsModalOpen(true);
							}}
							onDelete={(p) => {
								setSelectedPlan(p);
								setIsDeleteModalOpen(true);
							}}
						/>
					))}
				</div>
			) : (
				<div className="flex flex-col items-center justify-center rounded-[2rem] border border-dashed border-stone-200 bg-stone-50/50 py-24 text-center">
					<div className="mb-4 rounded-full bg-white p-4 text-stone-300 shadow-sm">
						<Search size={32} />
					</div>
					<h3 className="text-base font-bold text-stone-900">
						Nenhum plano encontrado
					</h3>
					<p className="mx-auto mt-1 max-w-xs text-sm leading-relaxed text-stone-400">
						Não encontramos planos com os critérios de busca ou
						ainda não existem planos cadastrados.
					</p>
				</div>
			)}

			<CompanyPlanModal
				isOpen={isModalOpen}
				onClose={() => {
					setIsModalOpen(false);
					setSelectedPlan(null);
				}}
				plan={selectedPlan}
			/>

			<DeleteModal
				isOpen={isDeleteModalOpen}
				onClose={() => {
					setIsDeleteModalOpen(false);
					setSelectedPlan(null);
				}}
				onConfirm={handleDeleteConfirm}
				title="Excluir plano"
				description={`Você tem certeza que deseja excluir o plano <strong>${selectedPlan?.name}</strong>?`}
			/>
		</div>
	);
};
